import type { ImageColumnProps } from '@modules/page-builder/types';
import ImageWithPaintPicker from '../../common/image-with-paint-picker/ImageWithPaintPicker';
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselPrevious,
    CarouselNext,
} from '@rocco/components/carousel';
import { ReactComponent as ArrowLeft } from '@rocco/icons/svg/arrow/arrow-chevron-left.svg';
import { ReactComponent as ArrowRight } from '@rocco/icons/svg/arrow/arrow-chevron-right.svg';
import { useState } from 'react';
import { HeartFill, HeartLine, Download2Line } from '@archipro-design/icons';
import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';
import { useTracker } from '@archipro-website/tracker';
import { encodeDownloadLink } from '../detail-single-image-shell/HoverButtonsImage';
import { usePermissions } from '@modules/user';
import { PermissionEnum } from 'generated/graphql';

/**
 * Props for the FullPageGalleryModule component.
 */
interface FullPageGalleryModuleProps {
    /**
     * An array of image column properties to display in the gallery.
     */
    columns: ImageColumnProps[];
}

/**
 * FullPageGalleryModule is a component that displays a carousel of images.
 * It filters out columns without an imageUrl and shows navigation arrows and a slide counter.
 */
const FullPageGalleryModule = ({ columns }: FullPageGalleryModuleProps) => {
    const renderedColumns = columns.filter((column) => {
        return column.imageUrl;
    });
    const [currentSlide, setCurrentSlide] = useState(1);
    const { isInDesignBoard, handleFollow } = useHandleFollow();
    const tracker = useTracker();
    const hasMarketingView = usePermissions(PermissionEnum.BmMarketingView);

    /**
     * Determines if an image should be preloaded based on current slide position
     * Preloads current and next image for better user experience
     */
    const shouldPreloadImage = (index: number) => {
        const currentIndex = currentSlide - 1; // Convert to 0-based index
        const nextIndex = (currentIndex + 1) % renderedColumns.length;
        return index === currentIndex || index === nextIndex;
    };

    return (
        <div className="mx-auto w-full md:max-w-455">
            <Carousel
                onChangeSlide={({ currentIndex }) =>
                    setCurrentSlide(currentIndex + 1)
                }
            >
                <div className="aspect-[1820/1014] h-fit overflow-hidden border">
                    <CarouselContent>
                        {renderedColumns.map((column, index) => (
                            <CarouselItem key={column.id}>
                                <div className="group relative md:w-455">
                                    <ImageWithPaintPicker
                                        width={column.dimension.width || 1820}
                                        height={column.dimension.height || 1014}
                                        src={column.imageUrl || ''}
                                        alt={
                                            column.caption ||
                                            `full page gallery image ${index + 1}`
                                        }
                                        className="aspect-[1820/1014] md:max-w-455"
                                        loading={
                                            shouldPreloadImage(index)
                                                ? 'eager'
                                                : 'lazy'
                                        }
                                        rel={
                                            shouldPreloadImage(index)
                                                ? 'preload'
                                                : undefined
                                        }
                                    />
                                    <div className="absolute right-0 top-0 hidden items-center justify-between gap-2 p-8 text-white opacity-0 transition-opacity duration-500 ease-in-out group-hover:opacity-100 md:flex">
                                        <button
                                            onClick={() => {
                                                handleFollow(
                                                    {
                                                        id:
                                                            column.libraryImage
                                                                ?.ID ?? 0,
                                                        libraryItemId:
                                                            column.libraryImage
                                                                ?.LibraryItemID ??
                                                            0,
                                                    },
                                                    'PhotoPin'
                                                );
                                            }}
                                            aria-label="Follow"
                                            className="flex items-center justify-center rounded-full bg-[#e5e5e5] p-3 hover:bg-[#f5f5f5]"
                                        >
                                            {isInDesignBoard(
                                                column.libraryImage?.ID ?? 0,
                                                'PhotoPin'
                                            ) ? (
                                                <HeartFill className="size-6" />
                                            ) : (
                                                <HeartLine className="size-6" />
                                            )}
                                        </button>
                                        {hasMarketingView &&
                                            column.libraryImage
                                                ?.DownloadLink && (
                                                <a
                                                    className="flex items-center justify-center rounded-full bg-[#e5e5e5] p-3 hover:bg-[#f5f5f5]"
                                                    href={encodeDownloadLink(
                                                        column.libraryImage
                                                            ?.DownloadLink || ''
                                                    )}
                                                    download
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        tracker.log(
                                                            'StaffDownloadImage',
                                                            {
                                                                url: new URL(
                                                                    window.location.href
                                                                ),
                                                                data: {
                                                                    ExtraData:
                                                                        column
                                                                            .libraryImage
                                                                            ?.ID ??
                                                                        0,
                                                                },
                                                            }
                                                        );
                                                    }}
                                                    aria-label="Download"
                                                >
                                                    <Download2Line className="size-6" />
                                                </a>
                                            )}
                                    </div>
                                </div>
                            </CarouselItem>
                        ))}
                    </CarouselContent>

                    <div className="absolute top-1/2 hidden h-20 w-full -translate-y-1/2 items-center justify-between px-7 md:flex">
                        <CarouselPrevious size="square42" aria-label="Previous">
                            <ArrowLeft className="size-5" />
                        </CarouselPrevious>
                        <CarouselNext size="square42" aria-label="Next">
                            <ArrowRight className="size-5" />
                        </CarouselNext>
                    </div>
                    {renderedColumns.length > 1 && (
                        <div className="absolute bottom-2 right-3 flex scale-75 items-center justify-between rounded-sm bg-black/70 px-2 py-0.5 text-sm text-white md:hidden">
                            {currentSlide} / {renderedColumns.length}
                        </div>
                    )}
                </div>
            </Carousel>
            {renderedColumns.length > 1 && (
                <h5 className="ml-0 hidden font-medium tracking-[0.02rem] text-black md:!block">
                    {`${currentSlide}/${renderedColumns.length}`}
                </h5>
            )}
        </div>
    );
};

export default FullPageGalleryModule;
